import type { AnyOrama } from '@orama/orama'
import type { OramaClient } from '@oramacloud/client'
import { batch } from '@preact/signals-core'
import { useSignal } from '@preact/signals-react'
import { useSignals } from '@preact/signals-react/runtime'
import r2wc from '@r2wc/react-to-web-component'
import { toCustomPropertiesString } from 'object-to-css-variables'
import { useEffect } from 'react'
import { Facets } from './components/Facets/Facets'
import { ResultsListing } from './components/ResultsListing/ResultsListing'
import type { ThemeConfig } from './defaultConfig'
import { searchboxConfig as defaultConfig } from './defaultConfig'
import * as $$ from './signal/searchbox'
import * as $ from './signal/searchresults'
import styles from './styles/searchresults.module.css'
import type { ColorScheme, DocsDocument, ReturningProps } from './types'

export interface RegisterSearchAllResultsParams {
  colorScheme?: string
  themeConfig?: ThemeConfig
  title?: string
  container?: string
  oramaInstance: AnyOrama | OramaClient
  resultsMap?: Partial<ReturningProps>
  facetProperty?: string
  renderItem?: (doc: DocsDocument) => React.ReactElement
}

const checkParams = (params: RegisterSearchAllResultsParams) => {
  if (!params || params.container) return

  batch(() => {
    params.colorScheme && params.colorScheme !== 'system' && ($.colorScheme.value = params.colorScheme as ColorScheme)
    params.colorScheme &&
      params.colorScheme === 'system' &&
      ($.colorScheme.value = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
    params.themeConfig &&
      ($.themeConfig.value = {
        '--font-primary': params.themeConfig['--font-primary'] ?? defaultConfig.ui.theme['--font-primary'],
        light: {
          ...defaultConfig.ui.theme.light,
          ...params.themeConfig.light
        },
        dark: {
          ...defaultConfig.ui.theme.dark,
          ...params.themeConfig.dark
        },
        system: {}
      })
    params.title && ($.title.value = params.title)
    params.oramaInstance && $$.setOramaClient(params.oramaInstance)
    params.resultsMap && $$.setSearchResultsMap(params.resultsMap)
    params.facetProperty && ($$.facetProperty.value = params.facetProperty)
    params.renderItem && ($$.renderItem.value = params.renderItem)
  })
}

export function SearchAllResults(props: RegisterSearchAllResultsParams) {
  useSignals()

  useEffect(() => {
    checkParams(props)
  }, [props])

  const colorScheme = useSignal($.colorScheme).value
  const queryTerm = new URLSearchParams(window.location.search).get('q')

  const themeStyle = toCustomPropertiesString($.themeConfig.value[colorScheme.value])

  const style = (
    <style>
      {`
      @import url('https://fonts.googleapis.com/css2?family=Instrument+Sans:ital,wght@0,400..700;1,400..700&display=swap');
      :root {
        ${themeStyle}
        --font-primary: "Instrument Sans", sans-serif;
        font-size: 100%;
      }
    `}
    </style>
  )

  useEffect(() => {
    if (!queryTerm) return
    $$.queryTerm.value = queryTerm
    $$.searchTerm.value = queryTerm
  }, [queryTerm])

  if (!queryTerm)
    return (
      <>
        {style}
        <h1>No results found</h1>
      </>
    )

  return (
    <div className={styles.searchResults}>
      {style}
      {/* Head */}
      <h1 className={styles.title}>
        {$.title.value} {queryTerm}
      </h1>
      <div className={styles.poweredBy}>
        <span>Powered by </span>
        <a
          href={`https://askorama.ai?utm_source=${window.location.hostname}`}
          target="_blank"
          rel="noreferrer"
          aria-label="Orama"
        >
          <img
            src={`https://website-assets.oramasearch.com/orama-when-${$.colorScheme.value}.svg`}
            alt="Powered by Orama"
            className={styles.logo}
          />
        </a>
      </div>

      <div className={styles.container}>
        <Facets facets={$$.facetsListing.value} className={styles.facets__list} />
        <ResultsListing />
      </div>
    </div>
  )
}

export function RegisterSearchAllResults(params: RegisterSearchAllResultsParams) {
  checkParams(params)
  if (!customElements.get('orama-search-results')) {
    return customElements.define('orama-search-results', r2wc(SearchAllResults))
  }
}
